import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

import { calculateDateRange } from "@/utils/date.utils";
import { validateReportCustomDateRange } from "@/components/blocks/GenerateReport/validations";
import { validateUniqueEmails } from "@/components/blocks/GenerateReport/validations";
import { MAX_EMAIL_LENGTH, MAX_EMAILS } from "@/common/report.common";
import { GenerateReportFormValues, DateRangePreset } from "@/types/report.types";
import { MAX_DATE_RANGE_YEARS, MAX_HISTORICAL_YEARS } from "@/common/report.common";
import { useActiveLocationsStore } from "@/stores/useActiveLocationsStore";

export interface ReportFormOptions {
  /** Fixed report type, for forms without a type picker. */
  reportType?: string;
  /** Recipients may be empty on submit; the caller checks them for the actions that email. */
  optionalRecipients?: boolean;
  /** Locations may be empty on submit, meaning every location the user has. */
  optionalLocations?: boolean;
  defaultDateRangePreset?: DateRangePreset;
}

export const useReportForm = (
  onSubmitCallback: (values: GenerateReportFormValues) => void,
  options: ReportFormOptions = {}
) => {
  const { t } = useTranslation();
  const { availableLocations } = useActiveLocationsStore();

  // Auto-select single location
  const defaultLocationIds = useMemo(() => {
    return availableLocations.length === 1 ? [availableLocations[0].locationId] : [];
  }, [availableLocations]);

  const generateReportSchema = z
    .object({
      reportType: z.string({ required_error: t("errors.required") }),
      locationIds: z
        .array(z.string())
        .min(options.optionalLocations ? 0 : 1, t("generateReport.validation.locationsRequired")),
      emailTo: z
        .array(z.string().email())
        .min(options.optionalRecipients ? 0 : 1, t("errors.required"))
        .max(
          MAX_EMAILS,
          t("generateReport.error.maxEmails", { maxEmails: MAX_EMAILS })
        )
        .refine(
          (value) =>
            !value?.find(
              (tagValue) =>
                z
                  .string()
                  .max(MAX_EMAIL_LENGTH)
                  .email(t("errors.invalidEmail"))
                  .safeParse(tagValue).error
            ),
          {
            message: t("errors.invalidEmail"),
          }
        )
        .refine((value) => validateUniqueEmails(value).isValid, {
          message: t("generateReport.error.duplicateEmail"),
        }),
      dateRangePreset: z.nativeEnum(DateRangePreset, {
        required_error: t("errors.required"),
      }),
      startDate: z.date().optional(),
      endDate: z.date().optional(),
    })
    .superRefine((data, ctx) => {
      // Validate date range
      const dateValidationResult = validateReportCustomDateRange(data);

      if (!dateValidationResult.isValid && dateValidationResult.path) {
        const errorParams: { [key: string]: string | number } = {};
        if (
          dateValidationResult.errorKey ===
          "generateReport.error.startDateTooFarPast"
        ) {
          errorParams.years = MAX_HISTORICAL_YEARS;
        }
        if (
          dateValidationResult.errorKey ===
          "generateReport.error.dateRangeTooLong"
        ) {
          errorParams.years = MAX_DATE_RANGE_YEARS;
        }

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            dateValidationResult.errorKey || "errors.unknown",
            errorParams
          ),
          path: dateValidationResult.path,
        });
      }

      // Validate unique emails
      if (data.emailTo && data.emailTo.length > 1) {
        const emailValidationResult = validateUniqueEmails(data.emailTo);
        if (!emailValidationResult.isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(emailValidationResult.errorKey || "errors.unknown"),
            path: ["emailTo"],
          });
        }
      }
    });

  // Initialize the form
  const form = useForm<z.infer<typeof generateReportSchema>>({
    resolver: zodResolver(generateReportSchema),
    defaultValues: {
      reportType: options.reportType,
      locationIds: defaultLocationIds,
      emailTo: [],
      dateRangePreset: options.defaultDateRangePreset,
      startDate: undefined,
      endDate: undefined,
    },
    mode: "all",
  });

  // Watch the preset selection to conditionally show date pickers
  const selectedDateRangePreset = form.watch("dateRangePreset");

  // Handle form submission
  const handleFormSubmit = (values: z.infer<typeof generateReportSchema>) => {
    let finalValues: GenerateReportFormValues = { ...values };

    // Calculate dates if a preset was used
    if (values.dateRangePreset !== DateRangePreset.CUSTOM) {
      const calculatedDates = calculateDateRange(values.dateRangePreset);
      if (calculatedDates) {
        finalValues.startDate = calculatedDates.startDate;
        finalValues.endDate = calculatedDates.endDate;
      } else {
        return;
      }
    }

    // Ensure dates are present before submitting
    if (!finalValues.startDate || !finalValues.endDate) {      
      return;
    }

    // Call the onSubmit callback passed to the hook
    onSubmitCallback(finalValues);
  };

  return {
    form,
    selectedDateRangePreset,
    handleFormSubmit,
    isCustomDateRange: selectedDateRangePreset === DateRangePreset.CUSTOM,
  };
}; 